/**
 * Miroir, côté boîte « Fin du direct », du passage de relais serveur
 * (`backend/src/lib/live-handover-plan.ts`).
 *
 * Un direct en fin manuelle déborde sur le créneau d'un direct de même source,
 * qui attend. À la coupure, la composition passe d'abord, puis ce direct part
 * à la fin de la composition et garde sa fin de grille. Le serveur reste seul
 * juge : ce calcul ne sert qu'à annoncer l'heure de départ à l'opérateur.
 */

/** Durée minimale d'antenne laissée au direct qui prend le relais (miroir serveur). */
export const LIVE_HANDOVER_MIN_SEC = 10

export interface LiveHandoverSlice {
  id:           string
  itemType:     string
  status:       string
  liveSourceId: string | null
  startTime:    string
  endTime:      string
}

export interface LiveHandoverPreview<T extends LiveHandoverSlice> {
  /** Direct qui prend le relais, `null` si tous les candidats sont consommés. */
  next:        T | null
  /** Départ annoncé du direct suivant : fin de la composition. */
  nextStartMs: number
  /** Directs de même source dont le créneau sera entièrement consommé. */
  skipped:     T[]
}

/**
 * `null` quand aucun direct de même source n'attend : la boîte garde alors son
 * fonctionnement habituel (trou borné par la prochaine ancre).
 */
export function previewLiveHandover<T extends LiveHandoverSlice>(input: {
  live:     { id: string; itemType: string; liveSourceId: string | null; liveEndMode?: string | null; startTime: string }
  cutAtMs:  number
  fillMs:   number
  items:    T[]
}): LiveHandoverPreview<T> | null {
  const { live } = input
  if (live.itemType !== 'LIVE' || !live.liveSourceId) return null
  if ((live.liveEndMode ?? 'AUTO') !== 'MANUAL') return null
  const liveStartMs = new Date(live.startTime).getTime()

  const candidates = input.items
    .filter(item =>
      item.id !== live.id
      && item.itemType === 'LIVE'
      && (item.status === 'SCHEDULED' || item.status === 'LOADING')
      && item.liveSourceId === live.liveSourceId
      && new Date(item.startTime).getTime() >= liveStartMs
      && new Date(item.startTime).getTime() < input.cutAtMs)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
  if (candidates.length === 0) return null

  const nextStartMs = input.cutAtMs + Math.max(0, input.fillMs)
  const skipped: T[] = []
  for (const item of candidates) {
    if (new Date(item.endTime).getTime() - nextStartMs < LIVE_HANDOVER_MIN_SEC * 1000) {
      skipped.push(item)
      continue
    }
    return { next: item, nextStartMs, skipped }
  }
  return { next: null, nextStartMs, skipped }
}
